import axiosIntents from '@/utils/axios-intents'
import StoreCache from '@/utils/StoreCache.js'
import mapRoamingAddon from '@/helpers/roaming-addon'
import { isExpiredTravelBalance, hasUsableDataBalance } from '@/helpers/travel-mobile-addon'

// Same 30-min TTL as the travel catalog
const roamingCache = new StoreCache(30 * 60 * 1000)

// In-flight request dedup so prepay/postpay mounts share one call
let roaming_addons_request = null
let roaming_regions_request = null

// Mobile the cached list was loaded for (prices are per customer/mobile)
let cached_product_uuid = null

// Apply discounts from Prices[] onto Entries in place (matched by availability UUID)
const applyPriceOverrides = (Entries, Prices) => {
    if (!Prices || !Prices.length) return

    Prices.forEach(price_item => {
        if (!(price_item.DiscountedPrice < price_item.OrigianlPrice)) return

        const entry = Entries.find(e => e.Availabilities?.[0]?.UUID == price_item.ProductID)
        if (entry && entry.Availabilities?.[0]) {
            entry.origianl_price = price_item.OrigianlPrice
            entry.discount = price_item.OrigianlPrice - price_item.DiscountedPrice
            entry.DiscountUUID = price_item.AppliedOfferID
            entry.Availabilities[0].RetailCents = price_item.DiscountedPrice
        }
    })
}

const sortByPriority = (a, b) => (a.Availabilities?.[0]?.Priority ?? 0) - (b.Availabilities?.[0]?.Priority ?? 0)

const state = {
    roaming_addons: [],
    // Set once fetched, so catalog sync triggers know to refetch
    roaming_addons_loaded: false,
    roaming_addons_loading: false,
    // Last fetch failed, so the catalog can offer "Try again"
    roaming_addons_error: false,
    // Region names from /catalog/v2/regions?Type=roaming, in BE order
    roaming_regions: [],
}

const getters = {
    roaming_addons: (state) => state.roaming_addons,
    roaming_addons_loaded: (state) => state.roaming_addons_loaded,
    roaming_addons_loading: (state) => state.roaming_addons_loading,
    roaming_addons_error: (state) => state.roaming_addons_error,
    roaming_regions: (state) => state.roaming_regions,

    // Owned roaming add-ons in the usage-balance response
    roaming_balances: (state, getters) =>
        (getters.customer_current_product_usage_products || []).filter(product => product.is_roaming),

    // Same State rule as travel: drop EXPIRED, keep PREACTIVE
    live_roaming_balances: (state, getters) =>
        getters.roaming_balances.filter(product => !isExpiredTravelBalance(product)),

    // Live roaming add-ons whose data can be shown on a widget
    usable_roaming_balances: (state, getters) =>
        getters.live_roaming_balances.filter(hasUsableDataBalance),
}

const mutations = {
    SAVE_ROAMING_ADDONS(state, roaming_addons) {
        state.roaming_addons = roaming_addons
        state.roaming_addons_loaded = true
        roamingCache.fix()
    },

    RESET_ROAMING_ADDONS(state) {
        state.roaming_addons = []
        state.roaming_addons_loaded = true
        roamingCache.reset()
    },

    SET_ROAMING_ADDONS_LOADING(state, is_loading) {
        state.roaming_addons_loading = is_loading
    },

    SET_ROAMING_ADDONS_ERROR(state, has_error) {
        state.roaming_addons_error = has_error
    },

    SAVE_ROAMING_REGIONS(state, roaming_regions) {
        state.roaming_regions = roaming_regions
    },
}

const actions = {
    // Roaming add-ons for the signed-in local customer. Send Roaming on every
    // call, otherwise the BE excludes them.
    GetRoamingAddons({ getters, commit, state }, { force = false } = {}) {
        const ProductInstanceUUID = getters.customer_current_product_uuid
        const is_same_product = cached_product_uuid == ProductInstanceUUID

        if (!force && is_same_product && !roamingCache.expired()) {
            return Promise.resolve(state.roaming_addons)
        }

        if (roaming_addons_request && is_same_product) return roaming_addons_request

        const ID = getters.current_account_uid
        if (!ID) return Promise.resolve([])

        const payload = {
            ProductTypeList: ['mobileAddon'],
            Roaming: 'only',
            SearchOptions: { PageSize: 1000 },
            CustomerInfo: { ID },
        }

        const ProductType = getters.accountType

        if (ProductType && ProductType != 'Not Activated') {
            payload.ProductInfo = { ProductType: `${ProductType}mobile` }
            if (ProductInstanceUUID) payload.ProductInfo.ProductInstanceUUID = ProductInstanceUUID
        }

        // Don't show the old mobile's list while the new one loads
        if (!is_same_product) commit('RESET_ROAMING_ADDONS')

        cached_product_uuid = ProductInstanceUUID

        commit('SET_ROAMING_ADDONS_LOADING', true)

        const request = axiosIntents.post('/catalog/v2/entries', payload)
            .then(({ data }) => {
                const { Entries = [], Prices } = data || {}

                applyPriceOverrides(Entries, Prices)

                const roaming_addons = Entries
                    .filter(entry => entry.IsRoaming)
                    .sort(sortByPriority)
                    .map(mapRoamingAddon)

                // Ignore a reply for a mobile the customer already left
                if (request === roaming_addons_request) {
                    commit('SAVE_ROAMING_ADDONS', roaming_addons)
                    commit('SET_ROAMING_ADDONS_ERROR', false)
                }

                return roaming_addons
            })
            .catch(error => {
                console.error('GetRoamingAddons Error', error)
                if (request === roaming_addons_request) {
                    commit('RESET_ROAMING_ADDONS')
                    commit('SET_ROAMING_ADDONS_ERROR', true)
                }
                return []
            })
            .finally(() => {
                if (request === roaming_addons_request) {
                    roaming_addons_request = null
                    commit('SET_ROAMING_ADDONS_LOADING', false)
                }
            })

        roaming_addons_request = request

        return request
    },

    // Region list for the roaming filter. On failure the filter falls back to
    // the regions found on the add-ons.
    GetRoamingRegions({ commit, state }) {
        if (state.roaming_regions.length) return Promise.resolve(state.roaming_regions)

        if (roaming_regions_request) return roaming_regions_request

        roaming_regions_request = axiosIntents.get('/catalog/v2/regions', { params: { Type: 'roaming' } })
            .then(({ data }) => {
                const roaming_regions = (data?.Regions || []).map(({ Region }) => Region).filter(Boolean)

                commit('SAVE_ROAMING_REGIONS', roaming_regions)

                return roaming_regions
            })
            .catch(error => {
                console.error('GetRoamingRegions Error', error)
                return []
            })
            .finally(() => {
                roaming_regions_request = null
            })

        return roaming_regions_request
    },
}

export default {
    state,
    getters,
    mutations,
    actions,
}
